const chapter = {
  id: 'purification-organic-compounds',
  slug: 'purification-organic-compounds',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE / NEET',
  title: {
    english: 'Purification & Characterisation of Organic Compounds',
        hindi: 'कार्बनिक यौगिकों का शुद्धिकरण और अभिलक्षण',
  },
  estReadMinutes: 35,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '1-2 questions in JEE / NEET',
        hindi: 'जेईई/नीट में 1-2 प्रश्न **पास्कल का नियम:** बंद तरल पदार्थ पर लगाया गया दबाव सभी भागों में बिना कम हुए प्रसारित होता है। हाइड्रोलिक प्रेस का आधार: ।',
  },
  syllabusTags: ['Organic Chemistry', 'Class-11', 'JEE Main', 'NEET'],
  sections: [
    {
      id: 'purification',
      title: {
        english: 'Methods of Purification',
        hindi: 'शुद्धिकरण की विधियाँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Filtration** — separates solid from liquid (insoluble impurity).',
        hindi: '**निस्पंदन** - ठोस को तरल (अघुलनशील अशुद्धता) से अलग करता है।',
            },
            {
              english: '**Crystallisation** — solid in hot solvent; cool → pure crystals; impurities stay in mother liquor. Differential solubility key.',
        hindi: '**क्रिस्टलीकरण** - गर्म विलायक में ठोस; ठंडा → शुद्ध क्रिस्टल; मातृ मदिरा में अशुद्धियाँ रहती हैं। विभेदक घुलनशीलता कुंजी.',
            },
            {
              english: '**Distillation** — separates two miscible liquids with sufficiently different boiling points (Δb.p. > 25 K).',
        hindi: '**आसवन** - पर्याप्त रूप से भिन्न क्वथनांक (Δb.p. > 25 K) वाले दो मिश्रणीय तरल पदार्थों को अलग करता है।',
            },
            {
              english: '**Fractional distillation** — close boiling points (Δb.p. < 25 K); fractionating column gives multiple vap-condensation cycles. Used for petroleum refining.',
        hindi: '**आंशिक आसवन** - क्वथनांक बंद करें (Δb.p. <25 K); फ्रैक्शनेटिंग कॉलम कई वीएपी-संक्षेपण चक्र देता है। पेट्रोलियम शोधन के लिए उपयोग किया जाता है।',
            },
            {
              english: '**Distillation under reduced pressure** — for liquids that decompose before boiling at atm pressure (glycerol, sugar syrup).',
        hindi: '**कम दबाव में आसवन** - उन तरल पदार्थों के लिए जो एटीएम दबाव (ग्लिसरॉल, चीनी सिरप) पर उबलने से पहले विघटित हो जाते हैं।',
            },
            {
              english: '**Steam distillation** — for liquids immiscible with water, slightly volatile in steam (aniline, nitrobenzene). Sum of partial pressures = atm; mixture distils below 100 °C.',
        hindi: '**भाप आसवन** - पानी के साथ अमिश्रणीय, भाप में थोड़ा अस्थिर (एनिलिन, नाइट्रोबेंजीन) तरल पदार्थों के लिए। आंशिक pressures = atm का योग; मिश्रण 100 डिग्री सेल्सियस से नीचे आसवित होता है।',
            },
            {
              english: '**Sublimation** — for solids that sublime (camphor, naphthalene, anthracene, $NH_4Cl$).',
        hindi: '**ऊर्ध्वपातन** - उन ठोस पदार्थों के लिए जो उर्ध्वपातित होते हैं (कपूर, नेफ़थलीन, एन्थ्रेसीन, $NH_4Cl$)।',
            },
            {
              english: '**Differential extraction** — solute partitions between two immiscible solvents (separating funnel). Coefficient $K = C_{org}/C_{aq}$.',
        hindi: '**विभेदक निष्कर्षण** - दो अमिश्रणीय सॉल्वैंट्स (पृथक फ़नल) के बीच विलेय विभाजन। गुणांक $K = C_{org}/C_{aq}$.',
            },
            {
              english: '**Chromatography** — based on differential adsorption (column, TLC) or partition (paper, GC, HPLC).',
        hindi: '**क्रोमैटोग्राफी** - अंतर सोखना (कॉलम, टीएलसी) या विभाजन (पेपर, जीसी, एचपीएलसी) पर आधारित। **कार्बन और हाइड्रोजन** - CuO के साथ गर्मी; चूने के पानी को दूधिया कर देता है; निर्जलित हो जाता है। नीला. **लैसेन का परीक्षण (सोडियम संलयन)** - आयनिक लवण बनाने के लिए Na के साथ यौगिक को संलयन करें:',
            },
          ],
        },
      ],
    },
    {
      id: 'qualitative',
      title: {
        english: 'Qualitative Analysis (Detection of Elements)',
        hindi: 'गुणात्मक विश्लेषण (तत्वों का पता लगाना)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Carbon & Hydrogen** — heat with CuO; $CO_2$ turns lime water milky; $H_2O$ turns anhyd. $CuSO_4$ blue.',
        hindi: '**कार्बन और हाइड्रोजन** - CuO के साथ गर्मी; $CO_2$ चूने के पानी को दूधिया कर देता है; $H_2O$ निर्जलित हो जाता है। $CuSO_4$ नीला.',
            },
            {
              english: '**Lassaigne\'s test (sodium fusion)** — fuse compound with Na to form ionic salts:',
        hindi: '**लासेग्ने परीक्षण (सोडियम संलयन)** - आयनिक लवण बनाने के लिए Na के साथ यौगिक को संलयन करें:',
            },
            {
              english: '• N → NaCN; S → Na₂S; X → NaX; both N+S → NaSCN.',
        hindi: '• एन → NaCN; एस → Na₂S; एक्स → NaX; दोनों N+S → NaSCN.',
            },
            {
              english: '• **Nitrogen test:** $FeSO_4 + NaOH$, then $H_2SO_4$ → Prussian blue $Fe_4[Fe(CN)_6]_3$.',
        hindi: '• **नाइट्रोजन परीक्षण:** $FeSO_4 + NaOH$, फिर $H_2SO_4$ → प्रशिया नीला $Fe_4[Fe(CN)_6]_3$।',
            },
            {
              english: '• **Sulphur test (1):** sodium nitroprusside → violet colour.',
        hindi: '• **सल्फर परीक्षण (1):** सोडियम नाइट्रोप्रासाइड → बैंगनी रंग।',
            },
            {
              english: '• **Sulphur test (2):** lead acetate → black ppt PbS.',
        hindi: '• **सल्फर परीक्षण (2):** लेड एसीटेट → ब्लैक पीपीटी पीबीएस।',
            },
            {
              english: '• **N + S together:** blood-red colour with $FeCl_3$ (forms $Fe(SCN)_3$).',
        hindi: '• **एन + एस एक साथ:** $FeCl_3$ के साथ रक्त-लाल रंग ($Fe(SCN)_3$ बनाता है)।',
            },
            {
              english: '• **Halogens:** acidify with $HNO_3$, add $AgNO_3$: white (Cl, sol. in $NH_3$), pale yellow (Br, sparingly), yellow (I, insol. in $NH_3$).',
        hindi: '• **हैलोजन:** $HNO_3$ के साथ अम्लीकृत करें, $AgNO_3$ जोड़ें: सफेद (Cl, sol. $NH_3$ में), हल्का पीला (Br, संयम से), पीला (I, insol. $NH_3$ में)।',
            },
            {
              english: '**Phosphorus** — fuse with $Na_2O_2$, dissolve, treat with conc. $HNO_3$ + ammonium molybdate → yellow ppt of ammonium phosphomolybdate.',
        hindi: '**फॉस्फोरस** - $Na_2O_2$ के साथ फ्यूज करें, घोलें, सान्द्रता से उपचारित करें। $HNO_3$ + अमोनियम मोलिब्डेट → अमोनियम फॉस्फोमोलिब्डेट का पीला पीपीटी।',
            },
          ],
        },
      ],
    },
    {
      id: 'quantitative-c-h-n',
      title: {
        english: 'Quantitative Estimation — C, H, N',
        hindi: 'मात्रात्मक आकलन — C, H, N',
      },
      blocks: [
        {
          type: 'paragraph',
          body: {
            english: '**Liebig method (C & H):** burn known mass $w$ of compound in stream of $O_2$ over CuO. Trap $H_2O$ in anhyd. $CaCl_2$ (mass $w_1$) and $CO_2$ in KOH (mass $w_2$).',
        hindi: '**लीबिग विधि (सी और एच):** यौगिक के ज्ञात द्रव्यमान $w$ को CuO के ऊपर $O_2$ की धारा में जलाएं। $H_2O$ को एनहाइड्र में फँसाएँ। $CaCl_2$ (द्रव्यमान $w_1$) और $CO_2$ KOH में (द्रव्यमान $w_2$)।',
          },
        },
        {
          type: 'equation',
          equation: '\\%\\,C = \\dfrac{12}{44} \\cdot \\dfrac{w_2}{w} \\cdot 100, \\quad \\%\\,H = \\dfrac{2}{18} \\cdot \\dfrac{w_1}{w} \\cdot 100',
        },
        {
          type: 'paragraph',
          body: {
            english: '**Dumas method (N):** heat with CuO in $CO_2$ atm; N converted to $N_2$ collected over KOH (absorbs $CO_2$). Volume of $N_2$ at STP gives mass.',
        hindi: '**डुमास विधि (एन):** $CO_2$ एटीएम में CuO के साथ गर्म करें; N को KOH पर एकत्र करके $N_2$ में परिवर्तित किया गया ($CO_2$ को अवशोषित करता है)। एसटीपी पर $N_2$ का आयतन द्रव्यमान देता है।',
          },
        },
        {
          type: 'paragraph',
          body: {
            english: '**Kjeldahl method (N):** heat compound with conc. $H_2SO_4$ + $K_2SO_4 + CuSO_4$ → N → $(NH_4)_2SO_4$. Add NaOH, distil $NH_3$ into known excess of std acid; back-titrate.',
        hindi: '**केजेल्डाहल विधि (एन):** सांद्रण के साथ ताप यौगिक। $H_2SO_4$ + $K_2SO_4 + CuSO_4$ → N → $(NH_4)_2SO_4$। NaOH जोड़ें, std एसिड की ज्ञात अतिरिक्त मात्रा में $NH_3$ डिस्टिल करें; बैक-टाइट्रेट।',
          },
        },
        {
          type: 'equation',
          equation: '\\%\\,N = \\dfrac{1.4 \\times N \\times V_{acid\\ used}}{w}',
        },
        {
          type: 'note',
          body: {
            english: 'Kjeldahl fails for $-NO_2, -N=N-, $ pyridine etc. (N not converted to ammonia).',
        hindi: 'केजेल्डहल $-NO_2, -N=N-, $ पाइरीडीन आदि के लिए विफल रहता है (N अमोनिया में परिवर्तित नहीं होता है)।',
          },
        },
      ],
    },
    {
      id: 'quantitative-other',
      title: {
        english: 'Quantitative — Halogens, S, P, O',
        hindi: 'मात्रात्मक — हैलोजन, S, P, O',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Halogen (Carius method):** heat with fuming $HNO_3$ + $AgNO_3$ in sealed Carius tube → $AgX$. $\\%\\,X = (\\text{at.\\,wt.\\,X} / 143.5 \\text{ or appropriate}) \\cdot (\\text{wt AgX}/w) \\cdot 100$.',
        hindi: '**हैलोजन (कैरियस विधि):** सीलबंद कैरिअस ट्यूब में $HNO_3$ + $AgNO_3$ को धूनी के साथ गर्म करें → $AgX$। $\\%\\,X = (\\text{at.\\,wt.\\,X} / 143.5 \\text{ या उपयुक्त}) \\cdot (\\text{wt AgX}/w) \\cdot 100$।',
            },
            {
              english: '**Sulphur (Carius):** oxidised to $H_2SO_4$, ppt as $BaSO_4$. $\\%\\,S = (32/233)(\\text{wt BaSO}_4/w) \\cdot 100$.',
        hindi: '**सल्फर (कैरियस):** $H_2SO_4$ में ऑक्सीकृत, पीपीटी $BaSO_4$ में। $\\\\%\\,S = (32/233)(\\text{wt BaSO}_4/w) \\cdot 100$. **फॉस्फोरस (कैरियस):** $H_2SO_4$ में ऑक्सीकृत, अमोनियम फॉस्फोमोलिब्डेट या $BaSO_4$ के रूप में पीपीटी। $\\\\%\\,S = (32/233)(\\text{wt BaSO}_4/w) \\cdot 100$. **ऑक्सीजन** - आमतौर पर **अंतर** (100 - अन्य % का योग) द्वारा। या सीधे: गर्म ग्रेफाइट के ऊपर से गुजरें → CO → मापें।',
            },
            {
              english: '**Phosphorus (Carius):** oxidised to $H_3PO_4$, ppt as ammonium phosphomolybdate or $Mg_2P_2O_7$. $\\%\\,P = (62/222)(\\text{wt}/w) \\cdot 100$.',
        hindi: '**फॉस्फोरस (कैरियस):** $H_3PO_4$ में ऑक्सीकृत, पीपीटी अमोनियम फॉस्फोमोलिब्डेट या $Mg_2P_2O_7$ के रूप में। $\\\\%\\,P = (62/222)(\\text{wt}/w) \\cdot 100$.',
            },
            {
              english: '**Oxygen** — usually by **difference** (100 – sum of other %). Or directly: pass over hot graphite → CO → measure.',
        hindi: '**ऑक्सीजन** - आमतौर पर **अंतर** से (100 - अन्य % का योग)। या सीधे: गर्म ग्रेफाइट के ऊपर से गुजरें → CO → मापें।',
            },
          ],
        },
      ],
    },
    {
      id: 'empirical-molecular',
      title: {
        english: 'Empirical & Molecular Formula',
        hindi: 'मूलानुपाती और आण्विक सूत्र',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'From % composition → divide each % by atomic mass → simplest mole ratio → empirical formula.',
        hindi: '% संरचना से → प्रत्येक % को परमाणु द्रव्यमान से विभाजित करें → सरलतम मोल अनुपात → अनुभवजन्य सूत्र।',
            },
            {
              english: 'Molecular formula = (Empirical formula) $\\times n$, where $n = M_{molecular} / M_{empirical}$.',
        hindi: 'आणविक सूत्र = (अनुभवजन्य सूत्र) $\\times n$, जहां $n = M_{आणविक} / M_{अनुभवजन्य}$.',
            },
          ],
        },
        {
          type: 'example',
          problem: {
            english: 'Compound contains C 40%, H 6.67%, O 53.33%. Molar mass 180. Find molecular formula.',
        hindi: 'यौगिक में C 40%, H 6.67%, O 53.33% होता है। मोलर द्रव्यमान 180. आणविक सूत्र ज्ञात करें।',
          },
          solution: {
            english: 'Moles: C 40/12 = 3.33, H 6.67/1 = 6.67, O 53.33/16 = 3.33. Ratio 1:2:1 → empirical $CH_2O$ (mass 30). $n = 180/30 = 6$ → **$C_6H_{12}O_6$**.',
        hindi: 'मोल्स: सी 40/12 = 3.33, एच 6.67/1 = 6.67, ओ 53.33/16 = 3.33। अनुपात 1:2:1 → अनुभवजन्य $CH_2O$ (द्रव्यमान 30)। $n = 180/30 = 6$ → **$C_6H_{12}O_6$**.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\%\\,C = (12/44)(w_2/w) \\cdot 100',
      desc: {
        english: 'Liebig',
        hindi: 'लिबिग',
      },
    },
    {
      latex: '\\%\\,H = (2/18)(w_1/w) \\cdot 100',
      desc: {
        english: 'Liebig',
        hindi: 'लिबिग',
      },
    },
    {
      latex: '\\%\\,N = 1.4 N V / w',
      desc: {
        english: 'Kjeldahl',
        hindi: 'केजेल्डाहल',
      },
    },
    {
      latex: '\\%\\,X_{Cl} = (35.5/143.5)(w_{AgCl}/w) \\cdot 100',
      desc: {
        english: 'Carius (Cl)',
        hindi: 'कैरियस (सीएल)',
      },
    },
    {
      latex: '\\%\\,S = (32/233)(w_{BaSO_4}/w) \\cdot 100',
      desc: {
        english: 'Carius (S)',
        hindi: 'कैरियस (एस)',
      },
    },
    {
      latex: '\\text{Mol. formula} = n \\times \\text{Emp. formula}',
      desc: {
        english: '',
        hindi: 'मोल. सूत्र = एन एम्प. FORMULA',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Lassaigne',
      problem: {
        english: 'Blood-red colour in Lassaigne\'s test indicates:',
        hindi: 'लासेन के परीक्षण में रक्त-लाल रंग इंगित करता है:',
      },
      answer: {
        english: 'Both N and S present (forms SCN⁻)',
        hindi: 'N और S दोनों मौजूद हैं (SCN⁻ बनाते हैं)',
      },
      solution: {
        english: 'NaSCN + FeCl₃ → Fe(SCN)₃ (red).',
        hindi: 'NaSCN + FeCl₃ → Fe(SCN)₃ (लाल)।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Kjeldahl',
      problem: {
        english: 'Kjeldahl method **cannot** be used for:',
        hindi: 'केजेल्डाहल विधि का उपयोग **नहीं** किया जा सकता है:',
      },
      answer: {
        english: 'Nitro compounds, azo compounds, pyridine',
        hindi: 'नाइट्रो यौगिक, एज़ो यौगिक, पाइरीडीन',
      },
      solution: {
        english: 'N is not converted to NH₃ by H₂SO₄ in these.',
        hindi: 'इनमें H₂SO₄ द्वारा N को NH₃ में परिवर्तित नहीं किया जाता है।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Steam',
      problem: {
        english: 'Steam distillation requires the substance to be:',
        hindi: 'भाप आसवन के लिए पदार्थ की आवश्यकता होती है:',
      },
      answer: {
        english: 'Immiscible with water, sufficient vapour pressure at 100 °C, non-reactive with steam',
        hindi: 'पानी के साथ अमिश्रणीय, 100 डिग्री सेल्सियस पर पर्याप्त वाष्प दबाव, भाप के साथ गैर-प्रतिक्रियाशील',
      },
      solution: {
        english: '',
        hindi: 'भाप आसवन उसी पदार्थ के लिए उपयुक्त है जो पानी से अमिश्रणीय हो, 100°C पर पर्याप्त वाष्प दाब रखता हो और भाप से अभिक्रिया न करता हो।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Empirical',
      problem: {
        english: 'A compound contains 92.3% C and 7.7% H. Empirical formula?',
        hindi: 'एक यौगिक में 92.3% C और 7.7% H होता है। अनुभवजन्य सूत्र?',
      },
      answer: {
        english: 'CH',
        hindi: 'सीएच',
      },
      solution: {
        english: 'C: 92.3/12 = 7.7; H: 7.7/1 = 7.7; ratio 1:1.',
        hindi: 'सी: 92.3/12 = 7.7; एच: 7.7/1 = 7.7; अनुपात 1:1.',
      },
    },
    {
      year: 2019,
      exam: 'JEE Main',
      tag: 'Carius',
      problem: {
        english: '$0.246$ g of compound gave $0.4654$ g of $AgCl$. Find % Cl.',
        hindi: '$0.246$ ग्राम यौगिक ने $0.4654$ ग्राम $AgCl$ दिया। % सीएल ज्ञात करें।',
      },
      answer: {
        english: '$\\approx 46.83\\%$',
        hindi: '$\\approx 46.83\\\\%$',
      },
      solution: {
        english: '$\\%Cl = (35.5/143.5)(0.4654/0.246)\\cdot 100$.',
        hindi: '$\\%Cl = (35.5/143.5)(0.4654/0.246)\\cdot 100$।',
      },
    },
  ],
  next: {
    slug: 'environmental-chemistry',
    title: 'Environmental Chemistry',
  },
};

export default chapter;
